import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  FlatList,
  Modal,
  Platform,
  useWindowDimensions,
  Animated,
  LayoutChangeEvent,
  Pressable,
  Image,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { Ionicons } from '@expo/vector-icons';
import { Colors, Spacing, Shadows, Radii, ControlHeight, Fonts, Typography } from '../constants';
import { optionListsService } from '../services/optionLists';
import { OptionListType, OptionItem } from '../types';

interface ComboSelectProps {
  label?: string;
  listType?: OptionListType;  // Optional when customOptions provided
  value: string;
  onValueChange: (value: string, isCustom: boolean) => void;
  allowCustom?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;  // Custom search input placeholder
  error?: string;
  style?: any;
  scope?: { type: string; value: string };
  // External options for cases like clubs
  customOptions?: OptionItem[];
}

const DROPDOWN_MAX_HEIGHT = 350;

// Option item component with hover support
interface OptionItemComponentProps {
  item: OptionItem;
  isSelected: boolean;
  onSelect: () => void;
}

const OptionItemComponent: React.FC<OptionItemComponentProps> = ({
  item,
  isSelected,
  onSelect,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Pressable
      style={[
        styles.optionItem,
        isSelected && styles.optionItemSelected,
        isHovered && !isSelected && styles.optionItemHovered,
      ]}
      onPress={onSelect}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
    >
      {item.imageUrl && (
        <Image
          source={{ uri: item.imageUrl }}
          style={styles.optionImage}
        />
      )}
      <Text
        style={[
          styles.optionText,
          isSelected && styles.optionTextSelected,
        ]}
      >
        {item.label}
      </Text>
    </Pressable>
  );
};

// Add custom button component with hover support
interface AddCustomButtonProps {
  searchText: string;
  onPress: () => void;
  t: (key: string, options?: any) => string;
}

const AddCustomButton: React.FC<AddCustomButtonProps> = ({
  searchText,
  onPress,
  t,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Pressable
      style={[
        styles.addCustomItem,
        isHovered && styles.addCustomItemHovered,
      ]}
      onPress={onPress}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
    >
      <Text style={styles.addCustomText}>
        + {t('components.comboSelect.addCustom', { value: searchText.trim() })}
      </Text>
    </Pressable>
  );
};

export const ComboSelect: React.FC<ComboSelectProps> = ({
  label,
  listType,
  value,
  onValueChange,
  allowCustom = true,
  placeholder,
  searchPlaceholder: searchPlaceholderProp,
  error,
  style,
  scope,
  customOptions,
}) => {
  const { t } = useTranslation();
  const { height: windowHeight } = useWindowDimensions();
  const [options, setOptions] = useState<OptionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const fadeAnim = useRef(new Animated.Value(0)).current;

  // For web dropdown positioning
  const [triggerLayout, setTriggerLayout] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
    pageY: number;
  } | null>(null);
  const triggerRef = useRef<View>(null);

  // Load options on mount
  useEffect(() => {
    // If customOptions provided, use them directly
    if (customOptions) {
      setOptions(customOptions);
      setLoading(false);
      return;
    }

    // If no listType, we can't load from service
    if (!listType) {
      console.warn('ComboSelect: No listType or customOptions provided');
      setLoading(false);
      return;
    }

    const loadOptions = async () => {
      try {
        const data = await optionListsService.getOptions(listType, scope);
        setOptions(data);
      } catch (err) {
        console.error('Failed to load options:', err);
      } finally {
        setLoading(false);
      }
    };
    loadOptions();
  }, [listType, scope, customOptions]);

  // Animate dropdown on open
  useEffect(() => {
    if (isOpen) {
      fadeAnim.setValue(0);
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 150,
        useNativeDriver: true,
      }).start();
    }
  }, [isOpen, fadeAnim]);

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!searchText.trim()) return options;
    const search = searchText.toLowerCase();
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(search) ||
        opt.value.toLowerCase().includes(search)
    );
  }, [options, searchText]);

  // Check if current search is a custom value
  const isCustomValue = useMemo(() => {
    if (!searchText.trim()) return false;
    const search = searchText.toLowerCase();
    return !options.some((opt) => opt.value.toLowerCase() === search);
  }, [options, searchText]);

  // Get display label for selected value
  const displayValue = useMemo(() => {
    const option = options.find((opt) => opt.value === value);
    return option?.label || value || '';
  }, [options, value]);

  // Get the full selected option (for imageUrl)
  const selectedOption = useMemo(() => {
    return options.find((opt) => opt.value === value);
  }, [options, value]);

  // Get search placeholder (explicit prop or fallback to generic)
  const searchPlaceholder = searchPlaceholderProp || t('common.search');

  const handleSelect = (selectedValue: string, custom: boolean) => {
    onValueChange(selectedValue, custom);
    setSearchText('');
    setIsOpen(false);
  };

  const handleAddCustom = () => {
    if (searchText.trim()) {
      handleSelect(searchText.trim(), true);
    }
  };

  // Close handler that resets search
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSearchText(''); // Reset search when closing
  }, []);

  const handleOpen = useCallback(() => {
    setSearchText(''); // Reset search when opening
    if (Platform.OS === 'web' && triggerRef.current) {
      // Measure trigger position for web dropdown
      (triggerRef.current as any).measureInWindow?.(
        (x: number, y: number, width: number, height: number) => {
          setTriggerLayout({ x, y, width, height, pageY: y });
          setIsOpen(true);
        }
      );
      // Fallback if measureInWindow not available
      if (!(triggerRef.current as any).measureInWindow) {
        setIsOpen(true);
      }
    } else {
      setIsOpen(true);
    }
  }, []);

  const handleTriggerLayout = useCallback((event: LayoutChangeEvent) => {
    if (Platform.OS === 'web') {
      const { width, height } = event.nativeEvent.layout;
      // Store basic dimensions, full position measured on open
      setTriggerLayout((prev) => ({
        ...prev,
        width,
        height,
        x: prev?.x ?? 0,
        y: prev?.y ?? 0,
        pageY: prev?.pageY ?? 0,
      }));
    }
  }, []);

  // Stop click propagation for web
  const handleContentClick = useCallback((e: any) => {
    if (Platform.OS === 'web') {
      e.stopPropagation();
      e.preventDefault?.();
    }
  }, []);

  const renderDropdownContent = () => (
    <View
      style={styles.dropdownContainer}
      // Stop touch events from propagating to overlay (mobile)
      onStartShouldSetResponder={() => true}
      onTouchEnd={(e) => e.stopPropagation()}
      // Web click handler
      {...(Platform.OS === 'web' && {
        onClick: handleContentClick,
      })}
    >
      <View style={styles.searchContainer}>
        <Ionicons name="search-outline" size={16} color={Colors.textLight} />
        <TextInput
          style={[
            styles.searchInput,
            isSearchFocused && styles.searchInputFocused,
          ]}
          value={searchText}
          onChangeText={setSearchText}
          placeholder={searchPlaceholder}
          placeholderTextColor={Colors.textLight}
          autoFocus
          onFocus={() => setIsSearchFocused(true)}
          onBlur={() => setIsSearchFocused(false)}
        />
      </View>

      <FlatList
        data={filteredOptions}
        keyExtractor={(item) => item.value}
        style={styles.optionsList}
        keyboardShouldPersistTaps="handled"
        renderItem={({ item }) => (
          <OptionItemComponent
            item={item}
            isSelected={item.value === value}
            onSelect={() => handleSelect(item.value, false)}
          />
        )}
        ListEmptyComponent={
          <Text style={styles.emptyText}>
            {loading ? t('common.loading') : t('common.noResults')}
          </Text>
        }
        ListFooterComponent={
          allowCustom && isCustomValue && searchText.trim() ? (
            <AddCustomButton
              searchText={searchText}
              onPress={handleAddCustom}
              t={t}
            />
          ) : null
        }
      />
    </View>
  );

  // Calculate dropdown position for web
  const getWebDropdownStyle = () => {
    if (!triggerLayout) return {};

    const spaceBelow =
      windowHeight - triggerLayout.pageY - triggerLayout.height - 20;
    const spaceAbove = triggerLayout.pageY - 20;
    const openBelow =
      spaceBelow >= DROPDOWN_MAX_HEIGHT || spaceBelow >= spaceAbove;

    return {
      position: 'absolute' as const,
      top: openBelow
        ? triggerLayout.pageY + triggerLayout.height + 4
        : undefined,
      bottom: !openBelow
        ? windowHeight - triggerLayout.pageY + 4
        : undefined,
      left: triggerLayout.x,
      minWidth: triggerLayout.width,
      maxWidth: Math.max(triggerLayout.width, 400),
    };
  };

  const renderWebDropdown = () => (
    <Modal
      visible={isOpen}
      transparent
      animationType="none"
      onRequestClose={handleClose}
    >
      <View
        style={styles.webOverlay}
        {...(Platform.OS === 'web' && {
          onClick: handleClose,
        })}
      >
        <Animated.View
          style={[
            styles.webDropdown,
            getWebDropdownStyle(),
            {
              opacity: fadeAnim,
              transform: [
                {
                  translateY: fadeAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [-8, 0],
                  }),
                },
              ],
            },
          ]}
          {...(Platform.OS === 'web' && {
            onClick: handleContentClick,
          })}
        >
          {renderDropdownContent()}
        </Animated.View>
      </View>
    </Modal>
  );

  const renderMobileModal = () => (
    <Modal
      visible={isOpen}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
    >
      <TouchableOpacity
        style={styles.modalOverlay}
        activeOpacity={1}
        onPress={handleClose}
      >
        <View
          style={[styles.modalContent, { maxHeight: windowHeight * 0.7 }]}
          // Stop touch events from propagating to overlay
          onStartShouldSetResponder={() => true}
          onTouchEnd={(e) => e.stopPropagation()}
        >
          {renderDropdownContent()}
        </View>
      </TouchableOpacity>
    </Modal>
  );

  return (
    <View style={[styles.container, style]}>
      {label ? <Text style={styles.label}>{label}</Text> : null}

      <View ref={triggerRef} onLayout={handleTriggerLayout}>
        <TouchableOpacity
          style={[
            styles.selectButton,
            isOpen && styles.selectButtonOpen,
            error && styles.selectButtonError,
          ]}
          onPress={handleOpen}
        >
          {selectedOption?.imageUrl && (
            <Image
              source={{ uri: selectedOption.imageUrl }}
              style={styles.selectedImage}
            />
          )}
          <Text
            style={[
              styles.selectText,
              !displayValue && styles.placeholderText,
            ]}
          >
            {displayValue || placeholder || t('common.select')}
          </Text>
          {!!value && !selectedOption && (
            <Text style={styles.customTag}>
              {t('components.comboSelect.custom', { defaultValue: 'Custom' })}
            </Text>
          )}
          <Ionicons
            name={isOpen ? 'chevron-up-outline' : 'chevron-down-outline'}
            size={16}
            color={Colors.textMedium}
          />
        </TouchableOpacity>
      </View>

      {error ? <Text style={styles.errorText}>{error}</Text> : null}

      {isOpen &&
        (Platform.OS === 'web' ? renderWebDropdown() : renderMobileModal())}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: Spacing.base,
  },
  label: {
    ...Typography.label,
    color: Colors.textDark,
    marginBottom: Spacing.sm,
  },
  selectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: Spacing.sm,
    borderWidth: 1,
    borderColor: Colors.borderLight,
    borderRadius: Radii.control,
    paddingLeft: Spacing.base,
    paddingRight: 14,
    paddingVertical: 0,
    backgroundColor: Colors.white,
    height: ControlHeight.md,
  },
  selectButtonOpen: {
    borderColor: Colors.primary,
    ...(Platform.OS === 'web' && Shadows.focusRing),
  },
  selectButtonError: {
    borderColor: Colors.error,
  },
  customTag: {
    fontFamily: Fonts.semiBold,
    fontSize: 10,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: Colors.accentStrong,
    backgroundColor: Colors.primaryLight,
    paddingHorizontal: 7,
    paddingVertical: 4,
    borderRadius: Radii.tag,
    overflow: 'hidden',
  },
  selectText: {
    fontFamily: Fonts.regular,
    fontSize: 15,
    color: Colors.text,
    flex: 1,
  },
  placeholderText: {
    color: Colors.textLight,
  },
  chevron: {
    fontSize: 12,
    color: Colors.textMedium,
    marginLeft: Spacing.sm,
  },
  errorText: {
    ...Typography.meta,
    color: Colors.error,
    marginTop: Spacing.xs,
  },
  // Mobile modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: Colors.overlay,
    justifyContent: 'center',
    alignItems: 'center',
    padding: Spacing.lg,
  },
  modalContent: {
    width: '100%',
    maxWidth: 400,
    minHeight: 200,
    backgroundColor: Colors.white,
    borderWidth: 1,
    borderColor: Colors.borderLight,
    borderRadius: Radii.card,
    overflow: 'hidden',
    ...Shadows.panel,
  },
  // Web dropdown styles
  webOverlay: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  webDropdown: {
    backgroundColor: Colors.white,
    borderWidth: 1,
    borderColor: Colors.borderLight,
    borderRadius: Radii.card,
    overflow: 'hidden',
    maxHeight: DROPDOWN_MAX_HEIGHT,
    ...Shadows.cardHover,
  },
  dropdownContainer: {
    flex: 1,
    minHeight: 150,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
    padding: Spacing.sm,
    backgroundColor: Colors.background,
    borderBottomWidth: 1,
    borderBottomColor: Colors.borderLight,
  },
  searchInput: {
    flex: 1,
    height: 32,
    backgroundColor: 'transparent',
    borderWidth: 0,
    fontFamily: Fonts.regular,
    fontSize: 14,
    color: Colors.text,
    ...(Platform.OS === 'web' &&
      ({
        outlineStyle: 'none',
      } as any)),
  },
  searchInputFocused: {},
  optionsList: {
    maxHeight: 250,
    padding: 6,
  },
  optionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: Spacing.md,
    paddingVertical: 10,
    borderRadius: Radii.thumb,
  },
  optionItemSelected: {
    backgroundColor: Colors.primaryLight,
  },
  optionItemHovered: {
    backgroundColor: Colors.background,
  },
  optionImage: {
    width: 24,
    height: 24,
    borderRadius: 12,
    resizeMode: 'contain',
  },
  selectedImage: {
    width: 24,
    height: 24,
    borderRadius: 12,
    resizeMode: 'contain',
  },
  optionText: {
    fontFamily: Fonts.regular,
    fontSize: 15,
    color: Colors.text,
  },
  optionTextSelected: {
    fontFamily: Fonts.semiBold,
    color: Colors.accentStrong,
  },
  emptyText: {
    padding: Spacing.base,
    textAlign: 'center',
    fontFamily: Fonts.regular,
    fontSize: 14,
    color: Colors.textLight,
  },
  addCustomItem: {
    paddingHorizontal: 14,
    paddingVertical: Spacing.md,
    backgroundColor: Colors.background,
    borderTopWidth: 1,
    borderTopColor: Colors.borderLight,
  },
  addCustomItemHovered: {
    backgroundColor: Colors.primaryLight,
  },
  addCustomText: {
    fontFamily: Fonts.semiBold,
    fontSize: 14,
    color: Colors.accentStrong,
  },
});
